import api from "lib/api";
import { CursorResults } from "lib/api/services/cursors";
import { SearchConfig } from "lib/api/types";
import jsonpointer from "lib/jsonpointer/jsonpointer";
import _ from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createAlertAction } from "data/app/appActions";
import { createErrorAlert } from "lib/api/errors";
import { useDispatch } from "react-redux";
import { AxiosError } from "axios";
import {
	SearchSelectOptionProps,
	onChangeSearchSelect,
} from "@alm/flow-ds/components";
import { Guid } from "@alm/flow-ds/utils";

type Props = SearchConfig & {
	multi?: boolean;
	limit?: number;
	searchOperator?: "contains" | "exact";
};

function handleResults(
	data: CursorResults,
	labelField: string,
	valueField?: string
) {
	return data.map(item => ({
		value:
			valueField && jsonpointer.has(item.data, valueField)
				? jsonpointer.get(item.data, valueField)
				: item.id,
		label: jsonpointer.has(item.data, labelField)
			? jsonpointer.get(item.data, labelField)
			: item.id,
	}));
}

function useSearchSelect(
	props: Props,
	data?: SearchSelectOptionProps | SearchSelectOptionProps[]
) {
	const TENANT: string = useMemo(
		() =>
			window.location.hostname.substring(
				0,
				window.location.hostname.indexOf(".")
			) || "",
		[]
	);

	const reduxDispatch = useDispatch();

	const [cursorId, setCursorId] = useState<undefined | string>();
	const [searchTerm, setSearchTerm] = useState("");
	const [value, setValue] = useState<
		SearchSelectOptionProps | SearchSelectOptionProps[] | null | undefined
	>(data);
	const [isEmu, setIsEmu] = useState<boolean>(false);

	const [cacheId, setCacheId] = useState([Guid()]);

	const labelPointer = useRef(props.label);
	const valuePointer = useRef(props.overwriteDataWith);

	// Our label is a jsonpointer, we convert it to use dot syntax
	// for our filter key and select array
	const labelKey = useRef(jsonpointer.pointerToId(props.label));

	const valueKey = useRef(
		props.overwriteDataWith
			? jsonpointer.pointerToId(props.overwriteDataWith)
			: undefined
	);

	/**
	 * Sets backend type for use in search, potentially replace with redux value in future
	 */
	const handleBackendType = useCallback(async () => {
		const backendType = await api.tenants.getBackendType(TENANT);
		if (backendType === "emu") setIsEmu(true);
	}, [TENANT]);

	useEffect(() => {
		handleBackendType();
	}, [handleBackendType]);

	const onChange = useCallback(
		(newValue: onChangeSearchSelect) => {
			if (Array.isArray(newValue)) {
				if (props.limit === undefined) {
					setValue([...newValue]);
				} else if (newValue.length <= props.limit) {
					setValue([...newValue]);
				}
			} else if (newValue) {
				setValue({ ...(newValue as SearchSelectOptionProps) });
			} else {
				setValue(newValue);
			}
		},
		[props.limit]
	);

	const search = useCallback(
		async term => {
			try {
				const payload = _.cloneDeep(props.payload);

				if (!labelPointer) {
					console.error("No label field.");
					return;
				}

				// Add our filter to any predefined filters
				if (term)
					payload.filter.AND.push({
						[labelKey.current]: {
							// Sets search operator based on prop given and backend type
							[props.searchOperator
								? props.searchOperator === "contains" && isEmu
									? "regex"
									: props.searchOperator
								: isEmu
								? "regex"
								: "contains"]: {
								value:
									(props.searchOperator === "contains" &&
										isEmu) ||
									(isEmu && !props.searchOperator)
										? `.*${term}.*`
										: props.searchOperator === "exact"
										? term
										: `*${term}`,
							},
						},
					});

				// Add a select array to our query payload with our label in it
				payload.select = [labelKey.current];

				// If we have an overwrite value then we need to add it to the select
				if (valueKey.current) payload.select.push(valueKey.current);

				const { results, hasMore, cursorId } = await api.cursors.create(
					payload
				);

				setSearchTerm(term);
				setCursorId(cursorId);

				return {
					options: handleResults(
						results,
						labelPointer.current,
						valuePointer.current
					),
					hasMore,
				};
			} catch (error) {
				const e = error as AxiosError;
				if (e.response || e.message)
					reduxDispatch(
						createAlertAction(
							createErrorAlert(e, "errorLoadingList")
						)
					);
				return {
					options: [],
				};
			}
		},
		[isEmu, props.payload, props.searchOperator, reduxDispatch]
	);

	const page = useCallback(async () => {
		try {
			if (cursorId) {
				const { results, hasMore } = await api.cursors.nextPage(
					cursorId,
					10
				);
				return {
					options: handleResults(
						results,
						labelPointer.current,
						valuePointer.current
					),
					hasMore,
				};
			}
		} catch (error) {
			const e = error as AxiosError;
			if (e.response || e.message)
				reduxDispatch(
					createAlertAction(createErrorAlert(e, "errorLoadingList"))
				);
			return {
				options: [],
			};
		}
	}, [cursorId, reduxDispatch]);

	/**
	 * Clears options and resets cursorId
	 */
	const handleClearCache = useCallback(() => {
		setCursorId(undefined);
		setCacheId([Guid()]);
	}, []);

	const loadPageOptions = useCallback(
		(term, options) => {
			// If we have a cursor id then we need to check determine
			// whether we're searching or paging
			if (cursorId) {
				// check if the incoming search term matches the one
				// we have stored
				if (term === searchTerm) {
					// If the terms match then we're paging
					return page();
				} else {
					// else we're creating a cursor
					return search(term);
				}
			}
			// if we have no cursor id then we are creating one with
			// a new search
			else {
				return search(term);
			}
		},
		[cursorId, page, search, searchTerm]
	);

	return {
		loadPageOptions,
		onChange,
		value,
		cacheId,
		handleClearCache,
	};
}

export default useSearchSelect;
